import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheck, FaUsers, FaUser, FaBuilding } from 'react-icons/fa';
import JsonLd from './shared/JsonLd';

const Booking = () => {
  const [copied, setCopied] = useState(false);

  const serviceSchema = [
    {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "AI Cohort Workshop",
      "description": "90‑minute group workshop for up to 10 people covering LLM fundamentals and practical AI tools.",
      "provider": {
        "@type": "Organization",
        "name": "Dreams AI Can Buy",
        "url": "https://dreamsaicanbuy.com",
        "logo": "https://dreamsaicanbuy.com/DAICB%20logo.png"
      },
      "offers": {
        "@type": "Offer",
        "priceCurrency": "GBP",
        "price": 125,
        "url": "https://dreamsaicanbuy.com/#booking",
        "availability": "https://schema.org/PreOrder"
      },
      "hasCourseInstance": [{
        "@type": "CourseInstance",
        "courseMode": "Online",
        "courseWorkload": "PT1H30M"
      }]
    },
    {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "AI Strategy Coaching Session",
      "description": "90‑minute personalised coaching to 10× your productivity.",
      "provider": {
        "@type": "Organization",
        "name": "Dreams AI Can Buy",
        "url": "https://dreamsaicanbuy.com",
        "logo": "https://dreamsaicanbuy.com/DAICB%20logo.png"
      },
      "offers": {
        "@type": "Offer",
        "priceCurrency": "GBP",
        "price": 252,
        "url": "https://dreamsaicanbuy.com/#booking",
        "availability": "https://schema.org/InStock"
      },
      "hasCourseInstance": [{
        "@type": "CourseInstance",
        "courseMode": "Online",
        "courseWorkload": "PT1H30M"
      }]
    },
    {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "Enterprise AI Lecture",
      "description": "Comprehensive AI strategy presentation and workshop tailored for organizations and teams.",
      "provider": {
        "@type": "Organization",
        "name": "Dreams AI Can Buy",
        "url": "https://dreamsaicanbuy.com",
        "logo": "https://dreamsaicanbuy.com/DAICB%20logo.png"
      },
      "offers": {
        "@type": "Offer",
        "priceCurrency": "GBP",
        "url": "https://dreamsaicanbuy.com/#booking",
        "availability": "https://schema.org/InStock"
      },
      "hasCourseInstance": [{
        "@type": "CourseInstance",
        "courseMode": "Online or In-Person"
      }]
    }
  ];

  const handleCopyCode = () => {
    navigator.clipboard.writeText('JUNE10').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy code: ', err);
    });
  };

  return (
    <BookingSection id="booking">
      <JsonLd data={serviceSchema}/>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <BookingGrid>
            {/* New Cohort Session Card */}
            <BookingCard>
              <ComingSoonRibbon>July 22nd 2025</ComingSoonRibbon>
              <CohortIcon>
                <FaUsers />
              </CohortIcon>
              <h2>Cohort Workshop</h2>
              <Price><del>£125</del> £112.50 <small>per person</small></Price>
              <DateTimeInfo>
                <strong>July 22nd, 2025 at 12:00 UTC</strong>
              </DateTimeInfo>
              <p>Master AI fundamentals with up to 10 peers in this intensive 90-minute workshop</p>
              
              <Features>
                <FeatureItem>
                  <FaCheck /> Master the fundamentals of ChatGPT, Claude, and other leading AI models
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Learn which AI tools work best for your specific needs and goals
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Write prompts that get you 10x better results (plus avoid costly mistakes)
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> See live demos of AI tools that save hours of research and work
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Build your first AI automation to handle repetitive tasks
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Create professional graphics and websites in minutes, not hours
                </FeatureItem>
              </Features>
              
              <CardDiscountSection>
                <DiscountCodeButton onClick={handleCopyCode} title={copied ? "Copied!" : "Copy Code"}>
                  {copied ? "Code copied" : <><strong>'JUNE10'</strong> 10% off copy code</>}
                </DiscountCodeButton>
              </CardDiscountSection>
              
              <BookButton href="https://buy.stripe.com/14A7sMaeZ8Qk50p9Zt8ww05" target="_blank" rel="noopener noreferrer">
                Book Now
              </BookButton>
            </BookingCard>

            {/* Existing Coaching Session Card */}
            <BookingCard>
              <Ribbon>10% OFF</Ribbon>
              <CohortIcon>
                <FaUser />
              </CohortIcon>
              <h2>One to One Session</h2>
              <Price><del>£275</del> £247.50</Price>
              <p>Get a personalised 90-minute AI coaching session tailored to your workflow and goals</p>
              
              <Features>
                <FeatureItem>
                  <FaCheck /> Custom assessment to identify your biggest AI opportunities
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Tailored training on the AI tools that matter most for your work
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Exclusive access to premium AI tools and insider techniques
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Get hands-on with cutting-edge AI models before your competitors
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Build a personalized AI workflow that saves you 2+ hours daily
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Keep everything: session recording, transcript, and custom action plan
                </FeatureItem>
              </Features>
              
              <CardDiscountSection>
                <DiscountCodeButton onClick={handleCopyCode} title={copied ? "Copied!" : "Copy Code"}>
                  {copied ? "Code copied" : <><strong>'JUNE10'</strong> 10% off copy code</>}
                </DiscountCodeButton>
              </CardDiscountSection>
              
              <BookButton href="https://buy.stripe.com/8x2bJ20Ep1nScsRb3x8ww03" target="_blank" rel="noopener noreferrer">
                Book Now
              </BookButton>
            </BookingCard>

            {/* Existing Build with Me Card */}
            <BookingCard>
              <CohortIcon>
                <FaBuilding />
              </CohortIcon>
              <h2>Enterprise Workshop</h2>
              <Price>£PoA</Price>
              <p>Comprehensive AI strategy presentation and workshop tailored for your organization</p>
              
              <Features>
                <FeatureItem>
                  <FaCheck /> Customized presentation for your industry and use cases
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> AI strategy roadmap development for your organization
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Team workshop on AI integration and implementation
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Risk assessment and compliance considerations
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Cost-benefit analysis and ROI projections
                </FeatureItem>
                <FeatureItem>
                  <FaCheck /> Follow-up consultation and ongoing support options
                </FeatureItem>
              </Features>
              
              <BookButton href="https://meetings-eu1.hubspot.com/riz-pabani" target="_blank" rel="noopener noreferrer">
                Book a Call
              </BookButton>
            </BookingCard>
          </BookingGrid>

        </motion.div>
      </div>
    </BookingSection>
  );
};

const BookingSection = styled.section`
  background: linear-gradient(90deg, #f8f9fa 0%, #f6f7f8 100%);
  padding: 5rem 0;
`;

const BookingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto 3rem auto;

  @media (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const BookingCard = styled.div`
  background-color: var(--white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: var(--shadow);
  text-align: center;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  h2 {
    color: var(--primary-color);
    margin-bottom: 1.5rem;
    font-size: 1.9rem;
    line-height: 1.2;
  }
  
  p {
    margin-bottom: 1.5rem;
    font-size: 1rem;
    line-height: 1.4;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    
    h2 {
      font-size: 1.6rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    
    h2 {
      font-size: 1.4rem;
    }
  }
`;

const Price = styled.div`
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;

  del {
    font-size: 1.6rem;
    font-weight: 400;
    color: #6c757d;
    margin-right: 0.5rem;
  }
  
  small {
    font-size: 1rem;
    font-weight: 400;
    color: #6c757d;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
    
    del {
      font-size: 1.3rem;
    }
  }
`;

const Features = styled.ul`
  list-style: none;
  margin: 1.5rem 0;
  text-align: left;
  flex-grow: 1;
`;

const FeatureItem = styled.li`
  margin-bottom: 0.75rem;
  font-size: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  line-height: 1.4;
  
  svg {
    color: var(--green);
    margin-top: 0.2rem;
    flex-shrink: 0;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 0.6rem;
  }
`;

const BookButton = styled.a`
  display: inline-block;
  background-color: var(--primary-color);
  color: var(--white);
  padding: 1rem 2rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 1.2rem;
  transition: var(--transition);
  margin-top: auto;
  
  &:hover {
    background-color: #003b8a;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 0.8rem 1.5rem;
  }
`;

const CardDiscountSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
  margin-bottom: 1rem;

  @media (min-width: 576px) {
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
  }
`;

const DiscountCodeButton = styled.button`
  background-color: #ffe0b3;
  color: #333;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 6px;
  font-size: 1.1rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  line-height: 1.5;
  cursor: pointer;
  transition: var(--transition);
  width: 100%;

  strong {
    color: var(--primary-color);
  }

  &:hover {
    background-color: #ffca8a;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 0.6rem 1rem;
  }
`;

const Ribbon = styled.div`
  position: absolute;
  top: 90px;
  right: -30px;
  background-color: #ff9800;
  color: white;
  padding: 8px 50px;
  transform: rotate(45deg);
  transform-origin: top right;
  font-weight: bold;
  font-size: 1rem;
  text-align: center;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
  min-width: 120px;

  @media (max-width: 1400px) {
    top: 80px;
    right: -35px;
    padding: 6px 45px;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 6px 35px;
    top: 70px;
    right: -25px;
  }
`;

const ComingSoonRibbon = styled(Ribbon)`
  top: 120px;
  background-color: #4CAF50;

  @media (max-width: 1400px) {
    top: 110px;
  }

  @media (max-width: 480px) {
    top: 100px;
  }
`;

const CohortIcon = styled.div`
  font-size: 2rem;
  color: var(--primary-color);
  margin-bottom: 0.75rem;
  
  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const DateTimeInfo = styled.div`
  font-size: 1.1rem;
  color: var(--primary-color);
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 6px;
  
  strong {
    font-weight: 600;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 0.6rem;
  }
`;

export default Booking;
